import React, { useState, useEffect } from 'react';
import { useDiscordAuth } from '../hooks/useDiscordAuth';
import './OwnerPanel.css';

/* ── Only this Discord user ID sees the panel ── */
const OWNER_ID = '1150215252940705852';

/* ── Tag colour presets ── */
const TAG_PRESETS = [
  { label: 'Announcement', color: '#4fa3e8' },
  { label: 'Release',      color: '#10b981' },
  { label: 'Update',       color: '#8b5cf6' },
  { label: 'Maintenance',  color: '#f59e0b' },
  { label: 'Fix',          color: '#ef4444' },
];

const ANN_TYPES = ['info','success','warning','error'];

function Tab({ label, active, onClick }) {
  return (
    <button className={`op-tab${active ? ' op-tab--active' : ''}`} onClick={onClick}>
      {label}
    </button>
  );
}

function CopyButton({ value, label = 'Copy JSON' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button className="op-copy-btn" onClick={copy}>
      {copied ? '✓ Copied!' : label}
    </button>
  );
}

/* ── Announcement composer ── */
function AnnouncementTab() {
  const [ann, setAnn] = useState({
    id: `ann-${Date.now()}`,
    title: '',
    message: '',
    type: 'info',
    cta: '',
    ctaUrl: '',
    active: true,
  });

  const json = JSON.stringify(ann, null, 2);

  return (
    <div className="op-section">
      <p className="op-hint">
        Fill in the fields, copy the JSON, paste it into <code>public/announcement.json</code> and push. Goes live within ~30s.
      </p>

      <div className="op-row">
        <div className="op-field">
          <label>ID (unique per announcement)</label>
          <input value={ann.id} onChange={e => setAnn(a => ({ ...a, id: e.target.value }))} />
        </div>
        <div className="op-field">
          <label>Type</label>
          <select value={ann.type} onChange={e => setAnn(a => ({ ...a, type: e.target.value }))}>
            {ANN_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      </div>

      <div className="op-field">
        <label>Title</label>
        <input value={ann.title} placeholder="e.g. Code Assist v2 is live"
          onChange={e => setAnn(a => ({ ...a, title: e.target.value }))} />
      </div>

      <div className="op-field">
        <label>Message</label>
        <textarea rows={3} value={ann.message} placeholder="Short description shown in the popup..."
          onChange={e => setAnn(a => ({ ...a, message: e.target.value }))} />
      </div>

      <div className="op-row">
        <div className="op-field">
          <label>CTA Button Text (optional)</label>
          <input value={ann.cta} placeholder="e.g. Try it now"
            onChange={e => setAnn(a => ({ ...a, cta: e.target.value }))} />
        </div>
        <div className="op-field">
          <label>CTA URL (optional)</label>
          <input value={ann.ctaUrl} placeholder="e.g. /code-assist/use/home"
            onChange={e => setAnn(a => ({ ...a, ctaUrl: e.target.value }))} />
        </div>
      </div>

      <div className="op-field op-field--row">
        <label>Active</label>
        <input type="checkbox" checked={ann.active}
          onChange={e => setAnn(a => ({ ...a, active: e.target.checked }))} />
        <span className="op-hint-inline">Uncheck to hide the popup without deleting the file</span>
      </div>

      <div className="op-preview">
        <div className={`op-preview__popup op-preview__popup--${ann.type}`}>
          <div className="op-preview__popup-content">
            {ann.title && <strong>{ann.title}</strong>}
            {ann.message && <span>{ann.message}</span>}
            {ann.cta && <a href={ann.ctaUrl}>{ann.cta} →</a>}
          </div>
          <span className="op-preview__label">Preview</span>
        </div>
      </div>

      <div className="op-json-block">
        <pre>{json}</pre>
        <CopyButton value={json} />
      </div>
    </div>
  );
}

/* ── Blog post composer ── */
function BlogTab() {
  const today = new Date().toISOString().split('T')[0];
  const [post, setPost] = useState({
    slug: '',
    title: '',
    date: today,
    tag: 'Announcement',
    tagColor: '#4fa3e8',
    excerpt: '',
    content: '',
  });

  const slugify = (str) =>
    str.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const postJson  = JSON.stringify(post, null, 2);
  const indexHint = `Add "${post.slug}" to the top of public/blog/index.json`;

  return (
    <div className="op-section">
      <p className="op-hint">
        Compose a post, copy the JSON to <code>public/blog/your-slug.json</code>, add the slug to <code>public/blog/index.json</code>, push.
      </p>

      <div className="op-field">
        <label>Title</label>
        <input value={post.title} placeholder="Post title"
          onChange={e => setPost(p => ({
            ...p, title: e.target.value, slug: slugify(e.target.value)
          }))} />
      </div>

      <div className="op-row">
        <div className="op-field">
          <label>Slug (auto-generated)</label>
          <input value={post.slug} onChange={e => setPost(p => ({ ...p, slug: slugify(e.target.value) }))} />
        </div>
        <div className="op-field">
          <label>Date</label>
          <input type="date" value={post.date}
            onChange={e => setPost(p => ({ ...p, date: e.target.value }))} />
        </div>
      </div>

      <div className="op-row">
        <div className="op-field">
          <label>Tag</label>
          <select value={post.tag}
            onChange={e => {
              const preset = TAG_PRESETS.find(t => t.label === e.target.value);
              setPost(p => ({ ...p, tag: e.target.value, tagColor: preset?.color || p.tagColor }));
            }}>
            {TAG_PRESETS.map(t => <option key={t.label} value={t.label}>{t.label}</option>)}
          </select>
        </div>
        <div className="op-field">
          <label>Tag Colour</label>
          <div className="op-color-row">
            <input type="color" value={post.tagColor}
              onChange={e => setPost(p => ({ ...p, tagColor: e.target.value }))} />
            <span style={{ color: post.tagColor, fontWeight: 700, fontSize: '0.85rem' }}>{post.tag}</span>
          </div>
        </div>
      </div>

      <div className="op-field">
        <label>Excerpt (shown on blog list)</label>
        <textarea rows={2} value={post.excerpt}
          onChange={e => setPost(p => ({ ...p, excerpt: e.target.value }))} />
      </div>

      <div className="op-field">
        <label>Content (supports ## headings, **bold**, `code`, [links](url))</label>
        <textarea rows={10} value={post.content} placeholder="Write your post here..."
          onChange={e => setPost(p => ({ ...p, content: e.target.value }))} />
      </div>

      <div className="op-json-block">
        <pre>{postJson}</pre>
        <div className="op-json-actions">
          <CopyButton value={postJson} label={`Copy → public/blog/${post.slug || 'slug'}.json`} />
          <CopyButton value={indexHint} label="Copy index hint" />
        </div>
      </div>
    </div>
  );
}

/* ── Site stats ── */
function StatsTab({ user }) {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const stats = [
    { label: 'Logged in as', value: user.username },
    { label: 'Discord ID',   value: user.id },
    { label: 'Role',         value: 'Owner' },
    { label: 'Server time',  value: time.toLocaleTimeString() },
    { label: 'Environment',  value: window.location.hostname === 'localhost' ? 'Development' : 'Production' },
    { label: 'Build',        value: process.env.REACT_APP_BUILD_TIME || 'N/A' },
  ];

  const quickLinks = [
    { label: 'Blog posts dir',     href: 'https://github.com/Noahtarp/permsystems-website-main/tree/main/public/blog' },
    { label: 'announcement.json',  href: 'https://github.com/Noahtarp/permsystems-website-main/blob/main/public/announcement.json' },
    { label: 'Netlify dashboard',  href: 'https://app.netlify.com' },
  ];

  return (
    <div className="op-section">
      <div className="op-stats">
        {stats.map(({ label, value }) => (
          <div key={label} className="op-stat">
            <span className="op-stat__label">{label}</span>
            <span className="op-stat__value">{value}</span>
          </div>
        ))}
      </div>

      <p className="op-section-title">Quick links</p>
      <div className="op-quick-links">
        {quickLinks.map(({ label, href }) => (
          <a key={label} href={href} target="_blank" rel="noopener noreferrer" className="op-quick-link">
            {label} →
          </a>
        ))}
      </div>

      <p className="op-section-title">Keyboard shortcuts</p>
      <div className="op-shortcuts">
        <div className="op-shortcut"><kbd>Shift</kbd>+<kbd>O</kbd> — Toggle owner panel</div>
        <div className="op-shortcut"><kbd>Esc</kbd> — Close panel</div>
      </div>
    </div>
  );
}

/* ── Main panel ── */
export default function OwnerPanel() {
  const { user } = useDiscordAuth();
  const [open, setOpen] = useState(false);
  const [tab, setTab]   = useState('stats');
  const isOwner = user && user.id === OWNER_ID;

  // Keyboard shortcut: Shift+O — always registered, no-ops if not owner
  useEffect(() => {
    const onKey = (e) => {
      if (!isOwner) return;
      if (e.shiftKey && e.key === 'O') setOpen(v => !v);
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOwner]);

  // Only render for the owner
  if (!isOwner) return null;

  return (
    <>
      {/* Trigger button — floating bottom-left */}
      <button
        className="op-trigger"
        onClick={() => setOpen(v => !v)}
        title="Owner panel (Shift+O)"
        aria-label="Owner panel"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <circle cx="9" cy="6" r="3.5" stroke="currentColor" strokeWidth="1.6"/>
          <path d="M2 16c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
        <span className="op-trigger__badge">Owner</span>
      </button>

      {/* Panel */}
      {open && (
        <div className="op-overlay" onClick={e => e.target === e.currentTarget && setOpen(false)}>
          <div className="op-panel">

            {/* Header */}
            <div className="op-header">
              <div className="op-header__left">
                <img src={user.avatar} alt={user.username} className="op-avatar" />
                <div>
                  <p className="op-header__name">{user.username}</p>
                  <p className="op-header__role">Site Owner</p>
                </div>
              </div>
              <button className="op-close" onClick={() => setOpen(false)} aria-label="Close">✕</button>
            </div>

            {/* Tabs */}
            <div className="op-tabs">
              <Tab label="Dashboard" active={tab === 'stats'}        onClick={() => setTab('stats')} />
              <Tab label="Announcement" active={tab === 'announcement'} onClick={() => setTab('announcement')} />
              <Tab label="New Post"  active={tab === 'blog'}         onClick={() => setTab('blog')} />
            </div>

            {/* Content */}
            <div className="op-body">
              {tab === 'stats'        && <StatsTab user={user} />}
              {tab === 'announcement' && <AnnouncementTab />}
              {tab === 'blog'         && <BlogTab />}
            </div>

          </div>
        </div>
      )}
    </>
  );
}
